import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Image as ImageIcon } from 'lucide-react';
import CONFIG from '../../config/config';

type Props = {
  onFilesDropped: (files: File[]) => void;
  isDisabled?: boolean;
  className?: string;
};

function DragDropZone({ onFilesDropped, isDisabled = false, className = '' }: Props) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDisabled) {
      setIsDragging(true);
    }
  }, [isDisabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (isDisabled) return;

      const files = Array.from(e.dataTransfer.files).filter((file) => {
        const extension = file.name.split('.').pop()?.toLowerCase();
        return extension && CONFIG.IMAGE_TYPES.includes(extension);
      });

      if (files.length > 0) {
        onFilesDropped(files);
      }
    },
    [isDisabled, onFilesDropped]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const files = Array.from(e.target.files);
        onFilesDropped(files);
      }
      // Reset input value to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [onFilesDropped]
  );

  const handleClick = useCallback(() => {
    if (!isDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [isDisabled]);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={CONFIG.IMAGE_TYPES.map(ext => `.${ext}`).join(',')}
        onChange={handleFileInputChange}
        className="hidden"
        disabled={isDisabled}
      />
      <div
        className={`
          relative w-full border-2 border-dashed rounded-lg p-12
          transition-all duration-300 ease-in-out
          ${isDragging
            ? 'border-primary-500 bg-primary-50 scale-[1.02]'
            : 'border-gray-300 bg-gray-50 hover:border-primary-400 hover:bg-primary-25'
          }
          ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${className}
        `}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <div className="flex flex-col items-center justify-center space-y-4 text-center">
          <div className={`
            p-4 rounded-full transition-all duration-300
            ${isDragging
              ? 'bg-primary-100 scale-110'
              : 'bg-gray-100'
            }
          `}>
            {isDragging ? (
              <Upload className="h-8 w-8 text-primary-600 animate-bounce" />
            ) : (
              <ImageIcon className="h-8 w-8 text-gray-400" />
            )}
          </div>
          <div className="space-y-2">
            <p className={`
              text-lg font-medium transition-colors duration-200
              ${isDragging ? 'text-primary-700' : 'text-gray-700'}
            `}>
              {isDragging
                ? t('store.drag_drop.active') || 'Drop images here'
                : t('store.drag_drop.placeholder') || 'Drag & drop images here, or click to select'
              }
            </p>
            <p className="text-sm text-gray-500">
              {t('store.drag_drop.hint') || 'Supports PNG, JPG, JPEG, WEBP'}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default DragDropZone;

